import * as React from "react";
import { useHistory } from "react-router-dom";
import Collapsible from "react-collapsible";
import { useFormik } from "formik";
import classNames from "classnames";

// Components
import QuestionProgressBar from "../QuestionProgressBar/QuestionProgressBar";
import Button from "../Button/Button";
import IconClose from "../Icons/IconClose";
import IconArrowDown from "../Icons/IconArrowDown";
import AutoFillOption from "../AutoFillOption/AutoFillOption";

// Misc.
import InitialValues from "../../types/InitialValues.interface";

// Style
import "./QuestionStickyHeader.scss";

interface QuestionStickyHeader {
  progress: number;
  categoryId: string;
  defaultValues: any;
  initialValues: InitialValues;
  setValues: React.Dispatch<React.SetStateAction<InitialValues>>;
}

const QuestionStickyHeader: React.FC<QuestionStickyHeader> = (
  props: QuestionStickyHeader
) => {
  const {
    progress,
    categoryId,
    defaultValues,
    initialValues,
    setValues,
  } = props;
  const history = useHistory();
  const [isToggleOpen, setIsToggleOpen] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      default: "safe",
    },
    onSubmit: (values) => {
      const { default: defaultValue } = values;
      setValues({
        ...initialValues,
        ...defaultValues[defaultValue],
      });
      setIsToggleOpen(false);
    },
  });

  const triggerElement = (
    <div className="inline-flex items-center cursor-pointer">
      <p className="text-dw-blue font-bold text-s tracking-wide">
        Need help filling out the policy
      </p>
      <IconArrowDown
        color="#002D5A"
        className="transition-transform ease-out duration-300"
        style={{
          transform: `${isToggleOpen ? "rotate(180deg)" : "rotate(0deg)"}`,
        }}
      />
    </div>
  );

  const iconClassNames = classNames(
    "icon-close cursor-pointer transition-opacity ease-out duration-300",
    {
      "opacity-0 pointer-events-none": isToggleOpen,
    }
  );

  return (
    <div className="question-sticky bg-dw-grey-01 relative z-40">
      <div className="question-sticky__container w-11/12 max-w-screen-xl mx-auto relative h-full pb-3">
        <IconClose
          className={iconClassNames}
          onClick={() => {
            if (
              // eslint-disable-next-line no-alert
              window.confirm(
                "Do you really want to close? All unsaved progress in this question will be lost."
              )
            ) {
              history.push(`/category/${categoryId}`);
            }
          }}
        />
        <Collapsible
          open={isToggleOpen}
          trigger={triggerElement}
          handleTriggerClick={() => setIsToggleOpen(!isToggleOpen)}
          easing="cubic-bezier(0.5, 1, 0.89, 1)"
        >
          <div>
            <p className="text-dw-grey-14 mt-2">
              If you don’t want to click through all policies, you can also
              choose a setting for a policy with given answers. We highly
              recommend to go through them anyway and individualize the policy
              for the needs of your organization. Please note that these
              policies with given answers are not totally finished, as you still
              have to give certain informations, e.g. names of people
              responsible for certain tasks.
            </p>
            <div className="">
              <form onSubmit={formik.handleSubmit} className="mt-3">
                <div className="grid gap-y-12 lg:gap-x-4 lg:grid-cols-2">
                  <AutoFillOption
                    id="safe"
                    label="Safe"
                    value={formik.values.default}
                    infoText="The safe version of a policy aims to set a security standard that is not perfect, but goes far beyond practices that are common today. The goal is to increase digital security in the organization, but in a way that is still practical and useable for the employees."
                    onChange={formik.handleChange}
                  />
                  <AutoFillOption
                    id="safer"
                    label="Safer"
                    value={formik.values.default}
                    infoText="The safer version of a policy aims to set a security standard that is best practice or goes even beyond it. It may be unpractical for employees in certain parts. "
                    onChange={formik.handleChange}
                  />
                  <AutoFillOption
                    id="safest"
                    label="Safest"
                    value={formik.values.default}
                    infoText="The safest version of a policy aims to set the highest security standard possible. Please note that this may lead to time-consuming, costly and unpractical security mechanisms. We recommend this mode for organizations with an outstanding need for digital security, who maintain their own IT infrastructure, and have enough know-how and personal resources to enforce the policy."
                    onChange={formik.handleChange}
                  />
                </div>
                <Button type="submit" className="mt-4">
                  Apply
                </Button>
              </form>
            </div>
          </div>
        </Collapsible>
      </div>
      <QuestionProgressBar progress={progress} />
    </div>
  );
};

export default QuestionStickyHeader;
